import apiClient from './client';
import type {
    Reward,
    RewardCreate,
    RewardUpdate,
    RewardPublicResponse,
    RewardRetranslateProgress,
    MessageResponse
} from '@/types/rewards';

// ========== REWARDS API ==========

/**
 * Listar todos os rewards do domínio atual
 * GET /api/v1/rewards
 */
export const getRewards = async (): Promise<Reward[]> => {
    const response = await apiClient.get<Reward[]>('/rewards');
    return response.data;
};

/**
 * Obter reward específico por ID com todas as traduções
 * GET /api/v1/rewards/{reward_id}
 */
export const getReward = async (rewardId: number): Promise<Reward> => {
    const response = await apiClient.get<Reward>(`/rewards/${rewardId}`);
    return response.data;
};

/**
 * Criar novo reward
 * As traduções serão geradas automaticamente via ChatGPT
 * POST /api/v1/rewards
 */
export const createReward = async (data: RewardCreate): Promise<Reward> => {
    const response = await apiClient.post<Reward>('/rewards', data);
    return response.data;
};

/**
 * Atualizar reward
 * Se title, description ou button_text forem alterados, as traduções serão regeneradas
 * PUT /api/v1/rewards/{reward_id}
 */
export const updateReward = async (rewardId: number, data: RewardUpdate): Promise<Reward> => {
    const response = await apiClient.put<Reward>(`/rewards/${rewardId}`, data);
    return response.data;
};

/**
 * Deletar reward (soft delete)
 * DELETE /api/v1/rewards/{reward_id}
 */
export const deleteReward = async (rewardId: number): Promise<MessageResponse> => {
    const response = await apiClient.delete<MessageResponse>(`/rewards/${rewardId}`);
    return response.data;
};

/**
 * Disparar retradução do reward nos idiomas que ele JÁ tem selecionados
 * (texto e, no quiz, perguntas/opções/loader/tela final).
 * Roda em segundo plano na API; acompanhe com getRetranslateStatus.
 * Gasta tokens da OpenAI — só chamar após confirmação dupla do usuário.
 * A API trava por reward: um segundo POST enquanto roda devolve o progresso
 * da execução atual com already_running=true, sem gastar nada.
 * POST /api/v1/rewards/{reward_id}/retranslate
 */
export const retranslateReward = async (rewardId: number): Promise<RewardRetranslateProgress> => {
    const response = await apiClient.post<RewardRetranslateProgress>(`/rewards/${rewardId}/retranslate`);
    return response.data;
};

/**
 * Andamento da retradução (done/total idiomas, falhas, tokens, status).
 * 404 quando não há nenhuma registrada (expira 1h após terminar).
 * GET /api/v1/rewards/{reward_id}/retranslate
 */
export const getRetranslateStatus = async (rewardId: number): Promise<RewardRetranslateProgress> => {
    const response = await apiClient.get<RewardRetranslateProgress>(`/rewards/${rewardId}/retranslate`);
    return response.data;
};

// ========== PUBLIC REWARDS API ==========

/**
 * Obter reward público com tradução específica (sem autenticação)
 * Este endpoint é otimizado com cache Redis
 * GET /api/v1/public/rewards/{reward_id}/{language}
 *
 * @param rewardId - ID do reward
 * @param language - Código do idioma (pt, es, en, it, jp, fr)
 */
export const getRewardPublic = async (
    rewardId: number,
    language: 'pt' | 'es' | 'en' | 'it' | 'jp' | 'fr'
): Promise<RewardPublicResponse> => {
    const response = await apiClient.get<RewardPublicResponse>(
        `/public/rewards/${rewardId}/${language}`
    );
    return response.data;
};

// ========== HELPER FUNCTIONS ==========

/**
 * Toggle do status is_closed do reward
 */
export const toggleRewardClosed = async (rewardId: number, isClosed: boolean): Promise<Reward> => {
    return updateReward(rewardId, { closed: isClosed });
};